/**
 * The Co-Living occupancy matrix (Slice 4).
 *
 * WHAT THIS REPLACES. Nine per-unit accordions, each of which had to be opened
 * before its rooms could be read, and each of which scrolled its own nine months
 * in its own little strip. Comparing two units meant opening both and scrolling
 * both. This is one table: units are groups, rooms are rows, months are columns,
 * and nothing has to be opened first. The per-unit collapse is deliberately gone
 * — see the report's UX-migration note.
 *
 * `components/RoomCalendar.js` IS UNTOUCHED and still serves
 * AdminUnitDetailPage, where one unit at a time is exactly the right shape. This
 * is a second view of the same truth for a page that shows many units at once.
 *
 * A REAL TABLE, because the thing on screen is a real matrix. A screen reader
 * announcing "Zimmer Rom, Okt 26" from the row and column headers is the whole
 * point; a grid of divs would announce a wall of labels instead.
 *
 * NOTHING THE OLD CALENDAR SHOWED IS DROPPED. The four figures it printed per
 * room and per unit — secure months, cancellable months, free months and the
 * potential loss — are trailing columns here, which also lines the unit's totals
 * up underneath its rooms' values instead of stranding them in a separate header.
 *
 * BELOW `sm` THE MATRIX IS REPLACED, NOT SHRUNK (UI5). Measured: at twelve
 * months the table is 1185px wide and 200px of that is the sticky room column,
 * so at a 500px viewport the content area is 466px and four and a half months
 * are readable — fewer than the smallest period the control offers. Showing the
 * default six months at once needs 200 + 6x60 = 560px of content, which a
 * viewport reaches at 592px; `sm` is the nearest breakpoint above it. Under it
 * `CoLivingOccupancyMobile` turns the axis instead. Both read the SAME `model`,
 * so there is one truth and two arrangements of it.
 *
 * THE CHROME IS NOT DUPLICATED. Heading, period control, legend, data-quality
 * notice and footnote are outside the swap and serve both — in particular there
 * is one `Zeitraum` select, so 6/9/12 cannot mean different things on the two.
 */
import React from "react";

import CoLivingOccupancyCell from "./CoLivingOccupancyCell";
import CoLivingOccupancyMobile from "./CoLivingOccupancyMobile";
import { useIsNarrowViewport } from "./useIsNarrowViewport";
import {
  DATA_QUALITY_NOTICE,
  FAMILY_LABELS,
  LEGEND_GROUPS,
  LIFECYCLE_LABELS,
  MONTH_COUNT_OPTIONS,
  UNKNOWN_STATE,
} from "./occupancyCalendarModel";
// R9 straight from the owner, as `CoLivingOccupancyCell` already does for the
// R8 transition helpers. The model barrel re-exports a fixed list; widening it
// would make a third production file carry a pass-through it has no other use
// for.
import {
  PARTIAL_LABEL,
  potentialLossDisplay,
} from "../../../utils/lifecycleCalendarTruth";

/** Swatch classes mirror the cell variants, minus the text colours. */
const SWATCH_CLASSES = {
  secure: "border-solid bg-emerald-500/15 border-emerald-500/40",
  cancellable: "border-dashed bg-emerald-500/10 border-emerald-500/50",
  always_cancellable: "border-dotted bg-emerald-500/[0.07] border-emerald-500/60",
  notice_given: "border-solid bg-emerald-500/15 border-emerald-500/40",
  reserved: "border-solid bg-amber-500/20 border-amber-500/45",
  free_planned: "border-dashed bg-rose-500/10 border-rose-500/45",
  free_unknown: "border-solid bg-rose-500/15 border-rose-500/35",
  [UNKNOWN_STATE]: "border-dashed bg-muted border-border",
};

/**
 * The legend names the seven BUSINESS states, grouped by family.
 *
 * Three swatches would hide the four occupied sub-states entirely, and those are
 * the distinctions the calendar exists to show.
 *
 * `unknown` is not in that list and must not be, because it is not a state a
 * room can be in — it says the data for a month could not be read. It appears
 * as its own "Datenstatus" group, and only when the matrix actually contains
 * one, so a healthy payload's legend stays the business vocabulary alone.
 */
function Legend({ showUnknown = false }) {
  const groups = showUnknown
    ? [...LEGEND_GROUPS, { family: UNKNOWN_STATE, states: [UNKNOWN_STATE] }]
    : LEGEND_GROUPS;

  return (
    <div className="flex flex-wrap gap-x-6 gap-y-3" data-calendar-legend>
      {groups.map((group) => (
        <div key={group.family} data-legend-family={group.family}>
          <p className="m-0 mb-1 text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
            {FAMILY_LABELS[group.family]}
          </p>
          <ul className="m-0 flex list-none flex-wrap gap-x-3 gap-y-1 p-0">
            {group.states.map((state) => (
              <li
                key={state}
                data-legend-state={state}
                className="flex items-center gap-1.5 text-[11px] text-muted-foreground"
              >
                <span
                  className={`inline-block h-3 w-3 shrink-0 rounded-[3px] border ${SWATCH_CLASSES[state]}`}
                  aria-hidden="true"
                />
                {LIFECYCLE_LABELS[state]}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

/** Shared by the sticky room column so nothing shows through underneath it. */
const STICKY_COL =
  "sticky left-0 z-10 w-[200px] min-w-[200px] max-w-[200px] border-r border-border bg-card";

/**
 * Where the month grid stops and the summary starts.
 *
 * The first summary column carries the heavier rule; the rest keep the hairline
 * that separates one figure from the next. One function so the header row and
 * both body rows cannot drift into three different boundaries.
 */
const SUMMARY_EDGE = (index) =>
  index === 0 ? "border-l-2 border-border" : "border-l border-border";

/** The four trailing figures, as one header block and one body block. */
const TRAILING_HEADERS = [
  { key: "secure", label: "Sicher" },
  { key: "cancellable", label: "Kündbar" },
  { key: "free", label: "Frei" },
];

/**
 * The potential-loss cell, for a unit row or a room row (R9 / P1-E).
 *
 * BOTH LEVELS, ONE COMPONENT. The unit figure is a roll-up of the room ones; a
 * manager comparing the two must not meet two different vocabularies for the
 * same gap.
 *
 * A COMPLETE FIGURE IS UNTOUCHED: amount, no badge, no title, no extra text —
 * byte for byte what it printed before R9. Only an incomplete one gains
 * anything, and never only a colour: `partiell` is in the visible text and the
 * gap is spelled out in the accessible name.
 *
 * `relative` stays on the cell for the reason the unit row documented before:
 * the `sr-only` text inside is absolutely positioned, and without a positioned
 * ancestor the table's scroll container cannot clip it (Slice 5, finding F1).
 */
function PotentialLossCell({ aggregates, totalAttr, className }) {
  const loss = potentialLossDisplay(aggregates);
  const attrs = { [totalAttr]: "lostRevenue", "data-loss-kind": loss.kind };
  return (
    <td {...attrs} title={loss.accessibleText || undefined} className={className}>
      {loss.amountLabel}
      {loss.partial ? (
        <span className="ml-[3px] font-normal opacity-80">· {PARTIAL_LABEL}</span>
      ) : null}
      {loss.partial ? (
        // The same second line as the unit-detail calendar, same wording, same
        // owner — a table cell simply stacks it rather than wrapping a flex row.
        <span
          data-loss-gap
          className="block text-[10px] font-normal leading-tight text-muted-foreground"
        >
          {loss.gapText}
        </span>
      ) : null}
      {loss.srText ? <span className="sr-only"> {loss.srText}</span> : null}
    </td>
  );
}

export default function CoLivingOccupancyCalendar({
  model,
  monthCount,
  onMonthCountChange,
  roomCount,
  emptyMessage,
}) {
  const { months, groups } = model;
  const dataQuality = model.dataQuality || {};
  // ONE of the two renders, never both — see `useIsNarrowViewport` for why this
  // is not `sm:hidden`. The model above is folded once either way.
  const narrow = useIsNarrowViewport();

  return (
    <section>
      <div className="mb-[10px] flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <h2 className="m-0 text-[15px] font-semibold leading-tight text-foreground">
            Belegungskalender
          </h2>
          <p className="m-0 mt-1 text-[12px] leading-snug text-muted-foreground">
            Belegung und Verfügbarkeit der Zimmer im Monatsverlauf.
          </p>
        </div>

        {/*
          ONE CONTROL, NOT A LABEL AND A BOX THAT HAPPEN TO BE ADJACENT (UI4).
          The label and the select now sit inside a single bordered group, so
          the period reads as one thing at the same weight as the table's own
          headers.

          IT IS STILL A `select`. A segmented control would look tidier and
          would be a semantic change: the period is a value with three options,
          `M1`-`M6` drive it through `getByLabelText("Zeitraum")` and
          `fireEvent.change`, and the 6/9/12 contract is not UI4's to restate
          in buttons. The chrome around it changed; the element, its id, its
          label and its values did not.
        */}
        <div className="flex shrink-0 items-center gap-2 rounded-[10px] border border-border bg-card py-1 pl-2.5 pr-1">
          <label
            htmlFor="coliving-calendar-period"
            className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground"
          >
            Zeitraum
          </label>
          <select
            id="coliving-calendar-period"
            value={monthCount}
            onChange={(e) => onMonthCountChange(Number(e.target.value))}
            className="cursor-pointer rounded-[6px] border-0 bg-transparent px-1 py-0.5 text-[12px] font-semibold text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {MONTH_COUNT_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n} Monate
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="overflow-hidden rounded-[12px] border border-border bg-card shadow-sm">
        {/* The legend is chrome above the data, and now says so with a muted
            surface instead of sharing the table's card white (UI4). */}
        <div className="border-b border-border bg-muted/30 px-4 py-3">
          <Legend showUnknown={Boolean(dataQuality.hasUnknown)} />
          {dataQuality.hasUnknown ? (
            <p
              className="m-0 mt-2.5 text-[11px] text-muted-foreground"
              data-calendar-data-quality
            >
              {DATA_QUALITY_NOTICE}
            </p>
          ) : null}
        </div>

        {groups.length === 0 ? (
          <p className="m-0 px-4 py-8 text-center text-[13px] text-muted-foreground">
            {emptyMessage}
          </p>
        ) : narrow ? (
          <CoLivingOccupancyMobile model={model} />
        ) : (
          <div
            role="region"
            aria-label="Belegungskalender"
            tabIndex={0}
            className="overflow-x-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          >
            {/*
              `table-fixed` is load-bearing, not cosmetic. Under the default
              auto layout the column widths are driven by the WIDEST LABEL, so
              introducing "Frei – Unbekannt" in S4.1 pushed the month columns
              from 62px to 68px and shoved "Pot. Verlust" off the right edge at
              1440px. Fixed layout makes the declared `w-[...]` below the actual
              widths, so a future label can never silently cost a column again.
            */}
            <table className="w-full table-fixed border-collapse text-[12px]">
              <caption className="sr-only">
                Belegung je Zimmer über {months.length} Monate, gruppiert nach
                Co-Living-Einheit.
              </caption>
              <thead>
                <tr className="border-b border-border">
                  <th
                    scope="col"
                    className={`${STICKY_COL} px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground`}
                  >
                    Zimmer
                  </th>
                  {months.map((m) => (
                    <th
                      key={m.key}
                      scope="col"
                      data-month-key={m.key}
                      className={`w-[60px] min-w-[60px] px-1 py-2 text-center text-[10px] font-semibold uppercase tracking-[0.04em] ${
                        m.isCurrent
                          ? "border-b-2 border-primary bg-primary/5 text-foreground"
                          : "text-muted-foreground"
                      }`}
                    >
                      {m.label}
                    </th>
                  ))}
                  {/*
                    THE SUMMARY IS A BLOCK, NOT THREE MORE MONTHS (UI4). Its
                    columns count whole periods and one of them is money, so
                    reading them in the same rhythm as the month grid invites
                    the eye to continue the timeline into them. A muted surface
                    and a heavier first separator say where the matrix stops.
                  */}
                  {TRAILING_HEADERS.map((h, i) => (
                    <th
                      key={h.key}
                      scope="col"
                      className={`w-[56px] min-w-[56px] ${SUMMARY_EDGE(i)} bg-muted/30 px-1 py-2 text-center text-[10px] font-semibold uppercase tracking-[0.04em] text-muted-foreground`}
                    >
                      {h.label}
                    </th>
                  ))}
                  <th
                    scope="col"
                    className="w-[96px] min-w-[96px] bg-muted/30 px-2 py-2 text-right text-[10px] font-semibold uppercase tracking-[0.04em] text-muted-foreground"
                  >
                    Pot. Verlust
                  </th>
                </tr>
              </thead>

              <tbody>
                {groups.map((group) => (
                  <React.Fragment key={group.unitId}>
                    {/* A BAND, SO THE HIERARCHY IS READABLE AT A GLANCE (UI4).
                        The axis is unit -> room and always was; what changed is
                        that the group row now looks like a heading for the rows
                        beneath it rather than a slightly greyer row among
                        them — a top rule closes the previous unit, and the
                        accent edge on the sticky column ties the band to the
                        id sitting in it. */}
                    <tr
                      className="border-b border-t border-border bg-muted/50"
                      data-unit-group={group.displayId}
                    >
                      <th
                        scope="colgroup"
                        className={`${STICKY_COL} border-l-2 border-l-primary/50 bg-muted/50 px-4 py-2 text-left font-normal`}
                      >
                        {/* Truncates like the room rows below it. Without this a
                            long unit id and street wrapped to four lines and made
                            one group row three times the height of its neighbours
                            (Slice 5, finding F3). The id keeps its width; only the
                            address gives way, and the title carries the full text. */}
                        <span
                          className="flex min-w-0 items-baseline gap-2"
                          title={[group.displayId, group.addressLine1].filter(Boolean).join(" · ")}
                        >
                          <span className="shrink-0 font-mono text-[12px] font-semibold text-foreground">
                            {group.displayId}
                          </span>
                          {group.addressLine1 ? (
                            <span className="min-w-0 truncate text-[11px] text-muted-foreground">
                              {group.addressLine1}
                            </span>
                          ) : null}
                        </span>
                      </th>
                      <td colSpan={months.length} className="px-2 py-2 text-[11px] text-muted-foreground">
                        {group.roomCount === 0 ? "Keine Rooms für diese Unit erfasst." : null}
                      </td>
                      {TRAILING_HEADERS.map((h, i) => (
                        <td
                          key={h.key}
                          data-unit-total={h.key}
                          className={`${SUMMARY_EDGE(i)} px-1 py-2 text-center text-[11px] font-semibold tabular-nums text-muted-foreground`}
                        >
                          {group.aggregates[h.key]}
                        </td>
                      ))}
                      {/*
                        `relative` is load-bearing. The `sr-only` hint inside is
                        absolutely positioned, so without a positioned ancestor its
                        containing block is the page itself and this table's scroll
                        container cannot clip it. At 390px that stretched the
                        document to 1013px and gave the whole page a horizontal
                        scrollbar (Slice 5, finding F1).
                      */}
                      <PotentialLossCell
                        aggregates={group.aggregates}
                        totalAttr="data-unit-total"
                        className="relative px-2 py-2 text-right text-[11px] font-semibold tabular-nums text-muted-foreground"
                      />
                    </tr>

                    {/* `group` + `group-hover` rather than a plain row hover:
                        the sticky column paints its own background over the
                        row's, so without it the one cell the eye is actually
                        following would be the one that does not light up. */}
                    {group.rooms.map((room) => (
                      <tr
                        key={room.key}
                        className="group border-b border-border transition-colors last:border-b-0 hover:bg-muted/40"
                        data-room-row={room.name}
                      >
                        <th
                          scope="row"
                          className={`${STICKY_COL} px-4 py-2 text-left font-normal group-hover:bg-muted/40`}
                        >
                          {/* ROOM-ACT-4. The marker sits beside the NAME, not
                              in a tooltip and not in a colour: a retired room
                              has no tenancy, so every month of its row reads
                              free and the row would otherwise present
                              stood-down stock as available capacity. It marks
                              the ROW only — the cells below are the backend's
                              month truth and are not restated here, because
                              `is_active` is a fact about today with no
                              deactivation date behind it. */}
                          {/* A <div>, not a <span>: UI4-28 pins that the FIRST
                              span inside this header truncates, which is how
                              a long room name gives way instead of widening a
                              fixed column. Wrapping the name in another span
                              would have quietly taken that contract away. */}
                          <div className="flex min-w-0 items-center gap-1.5">
                            <span className="min-w-0 truncate text-[12px] font-semibold text-foreground">
                              {room.name}
                            </span>
                            {room.isActive === false ? (
                              <span className="shrink-0 rounded-full border border-border bg-muted px-1.5 py-px text-[9px] font-semibold text-muted-foreground">
                                Inaktiv
                              </span>
                            ) : null}
                          </div>
                          <span className="block truncate text-[10px] text-muted-foreground" title={room.meta}>
                            {room.meta}
                          </span>
                        </th>
                        {room.cells.map((cell) => (
                          <CoLivingOccupancyCell
                            key={cell.monthKey}
                            cell={cell}
                            roomName={room.name}
                          />
                        ))}
                        {TRAILING_HEADERS.map((h, i) => (
                          <td
                            key={h.key}
                            data-room-total={h.key}
                            className={`${SUMMARY_EDGE(i)} px-1 text-center text-[11px] tabular-nums text-muted-foreground`}
                          >
                            {room.aggregates[h.key]}
                          </td>
                        ))}
                        {/* `relative` for the same reason as the unit row above. */}
                        <PotentialLossCell
                          aggregates={room.aggregates}
                          totalAttr="data-room-total"
                          className="relative px-2 text-right text-[11px] tabular-nums text-muted-foreground"
                        />
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {roomCount != null && groups.length > 0 ? (
        <p className="m-0 mt-2 text-[11px] text-muted-foreground" data-calendar-footnote>
          {roomCount === 1 ? "1 Zimmer" : `${roomCount} Zimmer`} über {months.length} Monate
        </p>
      ) : null}
    </section>
  );
}
